/**
 * Admin Dashboard Page Component
 */
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Shield, Users, TrendingUp, Activity, BarChart3, RefreshCw, Zap, Tv, Droplets, CheckCircle, Loader2, AlertTriangle, Wallet, ExternalLink, Landmark, Search } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../config';
import { ErrorState } from '../components/ErrorState';
import { classifyError } from '../lib/apiError';
import {
  getAdminApiHeaders,
  getAdminAuthToken,
  isAdminLockedError,
  fetchAdminTransactions,
  completeAdminTransaction,
  completeAllPendingAdminTransactions,
  fetchStuckPayments,
  retryStuckFulfillment,
  closeStuckAsRefunded,
  syncStuckTokenFromPaychangu,
  fetchSettlementReconciliation,
  syncSettlementCharges,
  markSettlements,
  settlePaychanguCollection,
  fetchAdminEarnings,
  fetchAdminUsers,
  toggleUserAdmin,
} from '../lib/adminApi';
import { useAppLock } from '../contexts/AppLockContext';


const AdminDashboard = () => {
  const { lock } = useAppLock();
  const [analytics, setAnalytics] = useState(null);
  const [adminTransactions, setAdminTransactions] = useState([]);
  const [stuckPayments, setStuckPayments] = useState(null);
  const [stuckLoading, setStuckLoading] = useState(false);
  const [retryingId, setRetryingId] = useState(null);
  const [closingRefundedId, setClosingRefundedId] = useState(null);
  const [syncingTokenId, setSyncingTokenId] = useState(null);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [transactionsError, setTransactionsError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [settlements, setSettlements] = useState(null);
  const [settlementsLoading, setSettlementsLoading] = useState(false);
  const [settlementsError, setSettlementsError] = useState(null);
  const [settlementFilter, setSettlementFilter] = useState('not_settled');
  const [selectedRefs, setSelectedRefs] = useState(new Set());
  const [settlementNote, setSettlementNote] = useState('');
  const [activeTab, setActiveTab] = useState("overview");
  const [earnings, setEarnings] = useState(null);
  const [earningsLoading, setEarningsLoading] = useState(false);
  const [earningsError, setEarningsError] = useState(null);
  const [stuckError, setStuckError] = useState(null);
  const [settlingCollection, setSettlingCollection] = useState(false);
  const [users, setUsers] = useState(null);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const [togglingAdminId, setTogglingAdminId] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerQueries, setCustomerQueries] = useState(null);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customerError, setCustomerError] = useState(null);

  useEffect(() => { fetchAnalytics(); }, []);

  useEffect(() => {
    if (activeTab === 'transactions') {
      loadAdminTransactions();
    }
    if (activeTab === 'stuck') {
      loadStuckPayments();
    }
    if (activeTab === 'settlements') {
      loadSettlements();
    }
    if (activeTab === 'earnings') {
      loadEarnings();
    }
    if (activeTab === 'users') {
      loadUsers();
    }
    if (activeTab === 'customer-queries') {
      loadCustomerQueries();
    }
  }, [activeTab, settlementFilter]);

  const fetchAnalytics = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const token = localStorage.getItem("auth_token");
      if (!token) {
        setLoadError("You are not signed in.");
        return;
      }
      const response = await axios.get(`${API_URL}/admin/analytics`, {
        headers: getAdminApiHeaders(token),
      });
      setAnalytics(response.data);
    } catch (error) {
      if (isAdminLockedError(error)) {
        lock();
        setLoadError("Admin access expired. Enter the master passphrase again.");
        toast.error("Admin session expired. Unlock again to load analytics.");
        return;
      }
      const detail = error.response?.data?.detail;
      const message =
        typeof detail === 'string'
          ? detail
          : error.response?.status === 403
            ? 'Admin access required'
            : 'Failed to load analytics';
      setLoadError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleAdminApiError = (error, fallbackMessage) => {
    if (isAdminLockedError(error)) {
      lock();
      toast.error('Admin session expired. Unlock again to continue.');
      return true;
    }
    const detail = error.response?.data?.detail;
    toast.error(typeof detail === 'string' ? detail : fallbackMessage);
    return false;
  };

  const loadEarnings = async () => {
    const token = getAdminAuthToken();
    if (!token) return;
    setEarningsLoading(true);
    setEarningsError(null);
    try {
      const data = await fetchAdminEarnings(token, { days: 90, limit: 100 });
      setEarnings(data);
    } catch (error) {
      handleAdminApiError(error, 'Failed to load earnings');
      setEarningsError(classifyError(error));
    } finally {
      setEarningsLoading(false);
    }
  };

  const loadAdminTransactions = async () => {
    const token = getAdminAuthToken();
    if (!token) return;
    setTransactionsLoading(true);
    setTransactionsError(null);
    try {
      const data = await fetchAdminTransactions(token, { limit: 100 });
      setAdminTransactions(data.transactions || []);
    } catch (error) {
      handleAdminApiError(error, 'Failed to load transactions');
      setTransactionsError(classifyError(error));
    } finally {
      setTransactionsLoading(false);
    }
  };

  const loadCustomerQueries = async (search = customerSearch) => {
    const token = getAdminAuthToken();
    if (!token) return;
    setCustomerLoading(true);
    setCustomerError(null);
    try {
      const data = await fetchAdminTransactions(token, {
        limit: 100,
        search: search?.trim() || undefined,
      });
      setCustomerQueries(data);
    } catch (error) {
      handleAdminApiError(error, 'Failed to load customer transactions');
      setCustomerError(classifyError(error));
    } finally {
      setCustomerLoading(false);
    }
  };

  const handleCustomerSearchSubmit = (event) => {
    event.preventDefault();
    loadCustomerQueries(customerSearch);
  };

  const handleCompleteOne = async (transactionId) => {
    const token = getAdminAuthToken();
    if (!token) return;
    setActionLoading(true);
    try {
      const result = await completeAdminTransaction(token, transactionId);
      toast.success(
        result.token
          ? `Transaction completed. Token: ${result.token}`
          : 'Transaction completed'
      );
      await Promise.all([fetchAnalytics(), loadAdminTransactions()]);
    } catch (error) {
      handleAdminApiError(error, 'Failed to complete transaction');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCompleteAllPending = async () => {
    const pending = adminTransactions.filter((t) => t.status === 'pending');
    if (pending.length === 0) {
      toast.info('No pending transactions');
      return;
    }
    if (!window.confirm(`Verify and complete all ${pending.length} pending transaction(s) with PayChangu? Only payments PayChangu confirms will be completed.`)) {
      return;
    }
    const token = getAdminAuthToken();
    if (!token) return;
    setActionLoading(true);
    try {
      const result = await completeAllPendingAdminTransactions(token);
      const count = result.completed_count ?? result.completed?.length ?? 0;
      toast.success(`Completed ${count} transaction(s)`);
      if (result.errors?.length) {
        toast.warning(`${result.errors.length} transaction(s) could not be completed`);
      }
      await Promise.all([fetchAnalytics(), loadAdminTransactions()]);
    } catch (error) {
      handleAdminApiError(error, 'Failed to complete pending transactions');
    } finally {
      setActionLoading(false);
    }
  };

  const pendingCount = adminTransactions.filter((t) => t.status === 'pending').length;
  const stuckCount = analytics?.transactions?.stuck_payments ?? stuckPayments?.count ?? 0;
  const notSettledBankCount =
    analytics?.transactions?.not_settled_bank ?? settlements?.reconciliation?.not_settled_count ?? 0;

  const loadSettlements = async () => {
    const token = getAdminAuthToken();
    if (!token) return;
    setSettlementsLoading(true);
    setSettlementsError(null);
    try {
      const params = { days: 90, limit: 200 };
      if (settlementFilter && settlementFilter !== 'all') {
        params.settlement_status = settlementFilter;
      }
      const data = await fetchSettlementReconciliation(token, params);
      setSettlements(data);
      setSelectedRefs(new Set());
    } catch (error) {
      handleAdminApiError(error, 'Failed to load settlement reconciliation');
      setSettlementsError(classifyError(error));
    } finally {
      setSettlementsLoading(false);
    }
  };

  const toggleSettlementSelect = (reference) => {
    setSelectedRefs((prev) => {
      const next = new Set(prev);
      if (next.has(reference)) next.delete(reference);
      else next.add(reference);
      return next;
    });
  };

  const handleSyncCharges = async () => {
    const token = getAdminAuthToken();
    if (!token) return;
    setActionLoading(true);
    try {
      const result = await syncSettlementCharges(token, {
        references: Array.from(selectedRefs),
        only_not_settled: selectedRefs.size === 0,
        limit: 40,
      });
      toast.success(`Synced ${result.updated_count ?? 0} transaction(s) from PayChangu`);
      if (result.errors?.length) {
        toast.warning(`${result.errors.length} could not be synced`);
      }
      await loadSettlements();
    } catch (error) {
      handleAdminApiError(error, 'Sync charges failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkSettled = async (markFailed = false) => {
    const token = getAdminAuthToken();
    if (!token) return;
    if (selectedRefs.size === 0) {
      toast.info('Select one or more transactions first');
      return;
    }
    setActionLoading(true);
    try {
      const result = await markSettlements(token, {
        references: Array.from(selectedRefs),
        note: settlementNote || undefined,
        mark_failed: markFailed,
      });
      toast.success(`Marked ${result.updated_count} as ${result.settlement_status}`);
      setSettlementNote('');
      await Promise.all([fetchAnalytics(), loadSettlements()]);
    } catch (error) {
      handleAdminApiError(error, 'Failed to update settlement status');
    } finally {
      setActionLoading(false);
    }
  };

  const loadStuckPayments = async () => {
    const token = getAdminAuthToken();
    if (!token) return;
    setStuckLoading(true);
    setStuckError(null);
    try {
      const data = await fetchStuckPayments(token, { limit: 100 });
      setStuckPayments(data);
    } catch (error) {
      handleAdminApiError(error, 'Failed to load stuck payments');
      setStuckError(classifyError(error));
    } finally {
      setStuckLoading(false);
    }
  };

  const handleSettlePaychanguCollection = async () => {
    const token = getAdminAuthToken();
    if (!token) return;
    setSettlingCollection(true);
    try {
      const result = await settlePaychanguCollection(token);
      if (result.settled) {
        toast.success(result.message || 'Collection moved to main balance');
      } else if (result.manual_required) {
        toast.info(
          result.message ||
            'PayChangu has no API to move collection → main. Open the PayChangu dashboard → Wallet and transfer collection to main, then retry stuck payments.'
        );
        if (result.dashboard_url) {
          window.open(result.dashboard_url, '_blank', 'noopener,noreferrer');
        }
      } else if (result.skipped) {
        toast.info(result.message || 'No collection balance to move');
      } else if (result.success === false) {
        toast.error(result.message || 'Could not move collection to main balance');
      } else {
        toast.success(result.message || 'Settlement check complete');
      }
      await loadStuckPayments();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail.message : detail;
      handleAdminApiError(error, msg || 'Could not move collection to main balance');
      if (detail?.manual_required || error.response?.data?.manual_required) {
        toast.info('Use PayChangu dashboard → Wallet to transfer collection to main balance.');
      }
    } finally {
      setSettlingCollection(false);
    }
  };

  const loadUsers = async (search = userSearch) => {
    const token = getAdminAuthToken();
    if (!token) return;
    setUsersLoading(true);
    setUsersError(null);
    try {
      const data = await fetchAdminUsers(token, { search: search?.trim() || undefined, limit: 50 });
      setUsers(data);
    } catch (error) {
      handleAdminApiError(error, 'Failed to load users');
      setUsersError(classifyError(error));
    } finally {
      setUsersLoading(false);
    }
  };

  const handleUserSearchSubmit = (event) => {
    event.preventDefault();
    loadUsers(userSearch);
  };

  const handleToggleAdmin = async (user) => {
    const token = getAdminAuthToken();
    if (!token) return;
    const verb = user.is_admin ? 'remove admin access from' : 'grant admin access to';
    if (!window.confirm(`Are you sure you want to ${verb} ${user.full_name || user.phone || user.email || 'this user'}?`)) {
      return;
    }
    setTogglingAdminId(user.id);
    try {
      const result = await toggleUserAdmin(token, user.id);
      setUsers((prev) => prev && {
        ...prev,
        users: prev.users.map((u) => (u.id === user.id ? { ...u, is_admin: result.is_admin } : u)),
      });
      toast.success(result.is_admin ? 'User is now an admin' : 'Admin access removed');
    } catch (error) {
      handleAdminApiError(error, 'Failed to update admin status');
    } finally {
      setTogglingAdminId(null);
    }
  };

  const handleRetryFulfillment = async (transactionId) => {
    const token = getAdminAuthToken();
    if (!token) return;
    setRetryingId(transactionId);
    try {
      const result = await retryStuckFulfillment(token, transactionId);
      toast.success(
        result.token
          ? `Token issued: ${result.token}`
          : result.message || 'Fulfillment succeeded'
      );
      await Promise.all([fetchAnalytics(), loadStuckPayments()]);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg =
        typeof detail === 'string'
          ? detail
          : typeof detail === 'object' && detail?.message
            ? detail.message
            : 'Fulfillment retry failed — check PayChangu wallet settlement';
      if (!handleAdminApiError(error, msg)) {
        toast.error(msg, { duration: 8000 });
      }
    } finally {
      setRetryingId(null);
    }
  };

  const handleSyncTokenFromPaychangu = async (transactionId) => {
    const token = getAdminAuthToken();
    if (!token) return;
    setSyncingTokenId(transactionId);
    try {
      const result = await syncStuckTokenFromPaychangu(token, transactionId);
      toast.success(
        result.token
          ? `Token synced: ${result.token}`
          : result.message || 'Token synced from PayChangu'
      );
      await Promise.all([fetchAnalytics(), loadStuckPayments()]);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg =
        typeof detail === 'string'
          ? detail
          : typeof detail === 'object' && detail?.message
            ? detail.message
            : 'Could not sync token from PayChangu';
      if (!handleAdminApiError(error, msg)) {
        toast.error(msg, { duration: 8000 });
      }
    } finally {
      setSyncingTokenId(null);
    }
  };

  const handleCloseAsRefunded = async (tx) => {
    const token = getAdminAuthToken();
    if (!token) return;
    const label = tx.reference || tx.id;
    if (
      !window.confirm(
        `Close ${label} as refunded?\n\nThis refunds the customer to their original payment method and closes the stuck case. No token will be issued.`
      )
    ) {
      return;
    }
    setClosingRefundedId(tx.id);
    try {
      const result = await closeStuckAsRefunded(token, tx.id);
      toast.success(result.message || 'Closed as refunded');
      await Promise.all([fetchAnalytics(), loadStuckPayments()]);
    } catch (error) {
      if (!handleAdminApiError(error, 'Failed to close as refunded')) {
        toast.error(error.response?.data?.detail || 'Failed to close as refunded');
      }
    } finally {
      setClosingRefundedId(null);
    }
  };

  const formatMwk = (n) =>
    n == null || Number.isNaN(Number(n)) ? '—' : `MWK ${Number(n).toLocaleString()}`;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 loading-shimmer rounded" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-32 loading-shimmer rounded-xl" />)}
        </div>
      </div>
    );
  }

  if (loadError && !analytics) {
    return (
      <div className="rounded-xl border border-border/50 bg-card">
        <ErrorState error={classifyError(loadError)} onRetry={fetchAnalytics} />
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="w-6 h-6 text-primary" /> Admin Dashboard
          </h1>
          <p className="text-muted-foreground">Monitor users and services</p>
        </div>
        <button onClick={fetchAnalytics} className="flex items-center gap-2 px-4 py-2 bg-muted rounded-lg hover:bg-muted/80">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card border border-border/50 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <p className="text-muted-foreground text-sm">Total Users</p>
              <p className="text-2xl font-bold">{analytics?.users?.total || 0}</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">+{analytics?.users?.new_last_30_days || 0} in last 30 days</p>
        </div>

        <div className="bg-card border border-border/50 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-emerald-500" />
            </div>
            <div>
              <p className="text-muted-foreground text-sm">Total Revenue</p>
              <p className="text-2xl font-bold">MWK {(analytics?.revenue?.total || 0).toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border/50 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center">
              <Activity className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="text-muted-foreground text-sm">Transactions</p>
              <p className="text-2xl font-bold">{analytics?.transactions?.total || 0}</p>
            </div>
          </div>
          <p className="text-xs text-emerald-500 mt-2">{analytics?.transactions?.successful || 0} successful</p>
          {(analytics?.transactions?.pending || 0) > 0 && (
            <p className="text-xs text-amber-500 mt-1">{analytics.transactions.pending} pending</p>
          )}
        </div>

        <div className="bg-card border border-border/50 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-yellow-500/20 rounded-xl flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-yellow-500" />
            </div>
            <div>
              <p className="text-muted-foreground text-sm">Success Rate</p>
              <p className="text-2xl font-bold">
                {analytics?.transactions?.total > 0 
                  ? Math.round((analytics.transactions.successful / analytics.transactions.total) * 100) 
                  : 0}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {(analytics?.transactions?.pending || 0) > 0 && activeTab === 'overview' && (
        <button
          type="button"
          onClick={() => {
            setActiveTab('transactions');
          }}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-600/90 text-sm font-medium"
        >
          <CheckCircle className="w-4 h-4" />
          Finish {analytics.transactions.pending} pending transaction{analytics.transactions.pending === 1 ? '' : 's'} — open Transactions
        </button>
      )}

      {stuckCount > 0 && activeTab === 'overview' && (
        <button
          type="button"
          onClick={() => setActiveTab('stuck')}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-amber-600 text-white rounded-xl hover:bg-amber-600/90 text-sm font-medium"
        >
          <AlertTriangle className="w-4 h-4" />
          {stuckCount} paid — token not issued — open Stuck payments
        </button>
      )}

      {notSettledBankCount > 0 && activeTab === 'overview' && (
        <button
          type="button"
          onClick={() => setActiveTab('settlements')}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-primary-foreground rounded-xl hover:bg-primary/90 text-sm font-medium"
        >
          <Landmark className="w-4 h-4" />
          {notSettledBankCount} payment(s) awaiting bank settlement — reconcile
        </button>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border/50 overflow-x-auto">
        {[
          { id: 'overview', label: 'Overview' },
          { id: 'earnings', label: 'Earnings' },
          { id: 'users', label: 'Users' },
          { id: 'customer-queries', label: 'Customer queries' },
          { id: 'transactions', label: 'Transactions' },
          { id: 'stuck', label: stuckCount > 0 ? `Stuck (${stuckCount})` : 'Stuck payments' },
          {
            id: 'settlements',
            label: notSettledBankCount > 0 ? `Settlements (${notSettledBankCount})` : 'Settlements',
          },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 font-medium transition-all whitespace-nowrap ${
              activeTab === tab.id
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-card border border-border/50 rounded-xl p-6">
            <h3 className="font-semibold mb-4">Service Breakdown</h3>
            {analytics?.service_breakdown?.length > 0 ? (
              <div className="space-y-3">
                {analytics.service_breakdown.map((service, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        service._id === "ESCOM" ? "bg-yellow-500/20" :
                        ["DSTV", "GOTV"].includes(service._id) ? "bg-blue-500/20" : "bg-cyan-500/20"
                      }`}>
                        {service._id === "ESCOM" ? <Zap className="w-4 h-4 text-yellow-500" /> :
                         ["DSTV", "GOTV"].includes(service._id) ? <Tv className="w-4 h-4 text-blue-500" /> :
                         <Droplets className="w-4 h-4 text-cyan-500" />}
                      </div>
                      <div>
                        <p className="font-medium">{service._id}</p>
                        <p className="text-xs text-muted-foreground">{service.count} transactions</p>
                      </div>
                    </div>
                    <p className="font-semibold">MWK {(service.revenue ?? 0).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">No data yet</p>
            )}
          </div>

          {/* Daily Stats */}
          <div className="bg-card border border-border/50 rounded-xl p-6">
            <h3 className="font-semibold mb-4">Last 7 Days</h3>
            {analytics?.daily_stats?.length > 0 ? (
              <div className="space-y-2">
                {analytics.daily_stats.map((day, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 border-b border-border/30 last:border-0">
                    <span className="text-muted-foreground">{day.day} ({day.date})</span>
                    <div className="text-right">
                      <p className="font-medium">{day.transactions} txns</p>
                      <p className="text-xs text-muted-foreground">MWK {day.revenue.toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">No data yet</p>
            )}
          </div>
        </div>
      )}

      {activeTab === 'earnings' && earningsError && !earnings && (
        <div className="bg-card border border-border/50 rounded-xl">
          <ErrorState error={earningsError} onRetry={loadEarnings} />
        </div>
      )}

      {activeTab === 'earnings' && (!earningsError || earnings) && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-card border border-border/50 rounded-xl p-5">
              <p className="text-sm text-muted-foreground">Platform fees (successful)</p>
              <p className="text-2xl font-bold mt-1">
                {formatMwk(earnings?.summary?.successful_only?.platform_fees_mwk)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {earnings?.platform_fee_percent ?? 4}% on utility amount
              </p>
            </div>
            <div className="bg-card border border-border/50 rounded-xl p-5">
              <p className="text-sm text-muted-foreground">Utility volume (successful)</p>
              <p className="text-2xl font-bold mt-1">
                {formatMwk(earnings?.summary?.successful_only?.utility_volume_mwk)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {earnings?.summary?.successful_only?.count ?? 0} completed transactions
              </p>
            </div>
            <div className="bg-card border border-border/50 rounded-xl p-5">
              <p className="text-sm text-muted-foreground">Total charged (successful)</p>
              <p className="text-2xl font-bold mt-1">
                {formatMwk(earnings?.summary?.successful_only?.total_charged_mwk)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Last {earnings?.period_days ?? 90} days</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-card border border-border/50 rounded-xl p-6">
              <h3 className="font-semibold mb-4">Fees by provider</h3>
              {earningsLoading ? (
                <div className="h-32 loading-shimmer rounded-lg" />
              ) : earnings?.by_provider?.length > 0 ? (
                <div className="space-y-3">
                  {earnings.by_provider.map((row) => (
                    <div key={row.provider_code} className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{row.provider_code}</p>
                        <p className="text-xs text-muted-foreground">{row.count} txns</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">{formatMwk(row.platform_fees_mwk)}</p>
                        <p className="text-xs text-muted-foreground">{formatMwk(row.utility_volume_mwk)} volume</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">No earnings data yet</p>
              )}
            </div>

            <div className="bg-card border border-border/50 rounded-xl p-6">
              <h3 className="font-semibold mb-4">Daily platform fees (30 days)</h3>
              {earningsLoading ? (
                <div className="h-32 loading-shimmer rounded-lg" />
              ) : earnings?.daily_fees?.length > 0 ? (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {earnings.daily_fees.map((day) => (
                    <div key={day.date} className="flex items-center justify-between py-2 border-b border-border/30 last:border-0">
                      <span className="text-muted-foreground">{day.day} ({day.date})</span>
                      <div className="text-right">
                        <p className="font-medium">{formatMwk(day.platform_fees_mwk)}</p>
                        <p className="text-xs text-muted-foreground">{day.transactions} txns</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">No daily data yet</p>
              )}
            </div>
          </div>

          <div className="bg-card border border-border/50 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">All transactions</h3>
              <button
                type="button"
                onClick={loadEarnings}
                disabled={earningsLoading}
                className="inline-flex items-center gap-2 text-sm text-primary"
              >
                <RefreshCw className={`w-4 h-4 ${earningsLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
            {earningsLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => <div key={i} className="h-12 loading-shimmer rounded-lg" />)}
              </div>
            ) : earnings?.transactions?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border/50">
                      <th className="text-left py-2 px-2">Reference</th>
                      <th className="text-left py-2 px-2">Provider</th>
                      <th className="text-right py-2 px-2">Utility</th>
                      <th className="text-right py-2 px-2">Fee</th>
                      <th className="text-right py-2 px-2">Charged</th>
                      <th className="text-left py-2 px-2">Status</th>
                      <th className="text-left py-2 px-2">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {earnings.transactions.map((tx) => (
                      <tr key={tx.id} className="border-b border-border/30 hover:bg-muted/20">
                        <td className="py-2 px-2 font-mono text-xs">{tx.reference}</td>
                        <td className="py-2 px-2">{tx.provider_code}</td>
                        <td className="py-2 px-2 text-right">{formatMwk(tx.utility_amount_mwk)}</td>
                        <td className="py-2 px-2 text-right text-emerald-600">{formatMwk(tx.platform_fee_mwk)}</td>
                        <td className="py-2 px-2 text-right">{formatMwk(tx.total_charged_mwk)}</td>
                        <td className="py-2 px-2">
                          <span className="px-2 py-0.5 text-xs rounded bg-muted">{tx.status}</span>
                        </td>
                        <td className="py-2 px-2 text-xs text-muted-foreground">
                          {tx.created_at ? new Date(tx.created_at).toLocaleDateString() : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">No transactions in this period.</p>
            )}
          </div>
        </div>
      )}

      {activeTab === "users" && (
        <div className="bg-card border border-border/50 rounded-xl p-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold">Users</h3>
              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-500/10 text-blue-500 text-xs font-medium rounded-full">
                <Users className="w-3.5 h-3.5" />
                {usersLoading && users == null
                  ? '…'
                  : userSearch && users?.total != null
                    ? `${users.total.toLocaleString()} matching`
                    : `${(users?.total ?? analytics?.users?.total ?? 0).toLocaleString()} total`}
              </span>
            </div>
            <form onSubmit={handleUserSearchSubmit} className="flex items-center gap-2">
              <input
                type="text"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder="Search by phone or email"
                className="px-3 py-1.5 text-sm rounded-lg border border-border/50 bg-background focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
              <button
                type="submit"
                disabled={usersLoading}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg bg-primary text-primary-foreground disabled:opacity-60"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${usersLoading ? 'animate-spin' : ''}`} />
                Search
              </button>
              {userSearch && (
                <button
                  type="button"
                  onClick={() => { setUserSearch(''); loadUsers(''); }}
                  className="text-sm text-muted-foreground hover:text-foreground"
                >
                  Clear
                </button>
              )}
            </form>
          </div>

          {usersError && !users ? (
            <ErrorState error={usersError} onRetry={() => loadUsers()} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border/50">
                    <th className="text-left py-3 px-4 text-muted-foreground font-medium">Name</th>
                    <th className="text-left py-3 px-4 text-muted-foreground font-medium">Phone</th>
                    <th className="text-left py-3 px-4 text-muted-foreground font-medium">Email</th>
                    <th className="text-left py-3 px-4 text-muted-foreground font-medium">Joined</th>
                    <th className="text-left py-3 px-4 text-muted-foreground font-medium">Role</th>
                    <th className="text-right py-3 px-4 text-muted-foreground font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {usersLoading && !users ? (
                    [1, 2, 3].map((i) => (
                      <tr key={i} className="border-b border-border/30">
                        <td colSpan={6} className="py-3 px-4"><div className="h-5 loading-shimmer rounded" /></td>
                      </tr>
                    ))
                  ) : users?.users?.length > 0 ? (
                    users.users.map((user) => (
                      <tr key={user.id} className="border-b border-border/30 hover:bg-muted/30">
                        <td className="py-3 px-4">{user.full_name || "—"}</td>
                        <td className="py-3 px-4 font-mono text-sm">{user.phone || "—"}</td>
                        <td className="py-3 px-4 text-sm text-muted-foreground">{user.email || "—"}</td>
                        <td className="py-3 px-4 text-muted-foreground">
                          {user.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}
                        </td>
                        <td className="py-3 px-4">
                          {user.is_admin ? (
                            <span className="px-2 py-1 bg-primary/20 text-primary text-xs rounded">Admin</span>
                          ) : (
                            <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded">User</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <button
                            type="button"
                            onClick={() => handleToggleAdmin(user)}
                            disabled={togglingAdminId === user.id}
                            className={`px-3 py-1 text-xs rounded-lg border transition-colors disabled:opacity-60 ${
                              user.is_admin
                                ? 'border-red-500/40 text-red-500 hover:bg-red-500/10'
                                : 'border-primary/40 text-primary hover:bg-primary/10'
                            }`}
                          >
                            {togglingAdminId === user.id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin inline" />
                            ) : user.is_admin ? (
                              'Remove admin'
                            ) : (
                              'Make admin'
                            )}
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="py-8 text-center text-muted-foreground">
                        {userSearch ? 'No user matches that phone or email.' : 'No users yet.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'stuck' && (
        <div className="space-y-4">
          <div className="bg-card border border-border/50 rounded-xl p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h3 className="font-semibold flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-primary" />
                  PayChangu wallet
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  <strong>Token delivery:</strong> checkout → collection; <code>bills/pay</code> uses main balance.
                  <br />
                  <strong>Bank payout:</strong> automatic T+1/T+2 after KYC + payout details in PayChangu (Settlements).
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {(stuckPayments?.wallet?.collection_balance ?? 0) > 0 && (
                  <button
                    type="button"
                    onClick={handleSettlePaychanguCollection}
                    disabled={settlingCollection || stuckLoading}
                    className="inline-flex items-center gap-2 px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-600/90 disabled:opacity-50"
                  >
                    {settlingCollection ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Wallet className="w-4 h-4" />
                    )}
                    Move collection → main
                  </button>
                )}
                <button
                  type="button"
                  onClick={loadStuckPayments}
                  disabled={stuckLoading}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-muted rounded-lg text-sm hover:bg-muted/80 disabled:opacity-50"
                >
                  {stuckLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  Refresh
                </button>
                <a
                  href={stuckPayments?.paychangu_dashboard_url || 'https://dashboard.paychangu.com'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-2 bg-primary text-primary-foreground rounded-lg text-sm hover:bg-primary/90"
                >
                  <ExternalLink className="w-4 h-4" />
                  PayChangu dashboard
                </a>
              </div>
            </div>

            {stuckLoading && !stuckPayments ? (
              <div className="mt-4 h-24 loading-shimmer rounded-lg" />
            ) : stuckPayments?.wallet?.success ? (
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="rounded-lg border border-border/50 p-4">
                  <p className="text-xs text-muted-foreground">Main balance (bill pay)</p>
                  <p className={`text-xl font-bold ${stuckPayments.wallet.negative_main ? 'text-red-600' : ''}`}>
                    {formatMwk(stuckPayments.wallet.main_balance)}
                  </p>
                </div>
                <div className="rounded-lg border border-border/50 p-4">
                  <p className="text-xs text-muted-foreground">Collection balance (checkout)</p>
                  <p className="text-xl font-bold">{formatMwk(stuckPayments.wallet.collection_balance)}</p>
                </div>
              </div>
            ) : (
              <p className="mt-4 text-sm text-muted-foreground">Could not load PayChangu wallet balance.</p>
            )}

            {stuckPayments?.wallet?.settlement_hints?.length > 0 && (
              <ul className="mt-4 space-y-2">
                {stuckPayments.wallet.settlement_hints.map((hint, i) => (
                  <li
                    key={i}
                    className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
                  >
                    {hint}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="bg-card border border-border/50 rounded-xl p-6">
            <h3 className="font-semibold flex items-center gap-2 mb-4">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              Stuck payments ({stuckPayments?.count ?? 0})
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Payment confirmed via PayChangu but no token yet. Ensure merchant wallet has spendable main balance for
              bill pay (dashboard). Bank settlement to your account is separate (T+1/T+2). If PayChangu already shows a
              token, use <strong>Sync token</strong> first. If you refunded in PayChangu, use <strong>Close refunded</strong>.
            </p>

            {stuckLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-16 loading-shimmer rounded-lg" />
                ))}
              </div>
            ) : stuckError && !stuckPayments ? (
              <ErrorState error={stuckError} onRetry={loadStuckPayments} />
            ) : stuckPayments?.transactions?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border/50">
                      <th className="text-left py-3 px-4 text-muted-foreground font-medium">Reference</th>
                      <th className="text-left py-3 px-4 text-muted-foreground font-medium">Account holder</th>
                      <th className="text-left py-3 px-4 text-muted-foreground font-medium">Service</th>
                      <th className="text-left py-3 px-4 text-muted-foreground font-medium">Utility</th>
                      <th className="text-left py-3 px-4 text-muted-foreground font-medium">Status</th>
                      <th className="text-left py-3 px-4 text-muted-foreground font-medium">Bank settlement</th>
                      <th className="text-left py-3 px-4 text-muted-foreground font-medium">Settlement</th>
                      <th className="text-left py-3 px-4 text-muted-foreground font-medium">Error</th>
                      <th className="text-right py-3 px-4 text-muted-foreground font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stuckPayments.transactions.map((tx) => (
                      <tr key={tx.id} className="border-b border-border/30 hover:bg-muted/30">
                        <td className="py-3 px-4 font-mono text-sm">{tx.reference}</td>
                        <td className="py-3 px-4 text-sm">{tx.customer_name || '—'}</td>
                        <td className="py-3 px-4">
                          <div>{tx.provider_code}</div>
                          <div className="text-xs text-muted-foreground font-mono">{tx.account_number || '—'}</div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="font-semibold">{formatMwk(tx.amount)}</div>
                          {tx.total != null && tx.total !== tx.amount && (
                            <div className="text-xs text-muted-foreground">
                              paid {formatMwk(tx.total)} incl. fee
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <span className="px-2 py-1 text-xs rounded bg-amber-500/20 text-amber-700">
                            {tx.status}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-xs text-muted-foreground">
                          {tx.settlement_status || 'not_settled'}
                        </td>
                        <td className="py-3 px-4 text-xs">
                          {tx.settlement?.likely_settlement_block ? (
                            <span className="text-amber-700">Needs collection → main</span>
                          ) : tx.settlement?.main_sufficient ? (
                            <span className="text-emerald-600">Main OK</span>
                          ) : (
                            <span className="text-muted-foreground">Low main</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-xs text-amber-800 max-w-[200px] truncate" title={tx.fulfillment_error}>
                          {tx.fulfillment_error || '—'}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="inline-flex flex-col sm:flex-row items-end sm:items-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => handleSyncTokenFromPaychangu(tx.id)}
                              disabled={
                                syncingTokenId === tx.id ||
                                retryingId === tx.id ||
                                closingRefundedId === tx.id
                              }
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-600 text-white text-xs rounded-lg hover:bg-emerald-600/90 disabled:opacity-50"
                            >
                              {syncingTokenId === tx.id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : null}
                              Sync token
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRetryFulfillment(tx.id)}
                              disabled={
                                retryingId === tx.id ||
                                closingRefundedId === tx.id ||
                                syncingTokenId === tx.id
                              }
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary text-primary-foreground text-xs rounded-lg hover:bg-primary/90 disabled:opacity-50"
                            >
                              {retryingId === tx.id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <RefreshCw className="w-3 h-3" />
                              )}
                              Retry token
                            </button>
                            <button
                              type="button"
                              onClick={() => handleCloseAsRefunded(tx)}
                              disabled={
                                closingRefundedId === tx.id ||
                                retryingId === tx.id ||
                                syncingTokenId === tx.id
                              }
                              className="inline-flex items-center gap-1 px-3 py-1.5 border border-border text-xs rounded-lg hover:bg-muted/80 disabled:opacity-50"
                            >
                              {closingRefundedId === tx.id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : null}
                              Close refunded
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">No stuck payments — all tokens issued or unpaid.</p>
            )}
          </div>
        </div>
      )}

      {activeTab === 'settlements' && (
        <div className="space-y-4">
          <div className="bg-card border border-border/50 rounded-xl p-6">
            <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
              <div>
                <h3 className="font-semibold flex items-center gap-2">
                  <Landmark className="w-5 h-5 text-primary" />
                  Bank settlement reconciliation
                </h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                  Match PayChangu dashboard / your bank statement, then mark rows settled. Sync pulls
                  charges from PayChangu verify-payment (no bulk settlements API).
                </p>
              </div>
              <a
                href={settlements?.paychangu_dashboard_url || 'https://dashboard.paychangu.com'}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3 py-2 bg-primary text-primary-foreground rounded-lg text-sm"
              >
                <ExternalLink className="w-4 h-4" /> PayChangu settlements
              </a>
            </div>

            {settlements?.wallet?.success && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <div className="rounded-lg border border-border/50 p-3">
                  <p className="text-xs text-muted-foreground">Not settled (bank)</p>
                  <p className="text-lg font-bold">{settlements.reconciliation?.not_settled_count ?? 0}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatMwk(settlements.reconciliation?.not_settled_charged_mwk)}
                  </p>
                </div>
                <div className="rounded-lg border border-border/50 p-3">
                  <p className="text-xs text-muted-foreground">Settled (bank)</p>
                  <p className="text-lg font-bold">{settlements.reconciliation?.settled_count ?? 0}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatMwk(settlements.reconciliation?.settled_charged_mwk)}
                  </p>
                </div>
                <div className="rounded-lg border border-border/50 p-3">
                  <p className="text-xs text-muted-foreground">Platform fees (open)</p>
                  <p className="text-lg font-bold">
                    {formatMwk(settlements.reconciliation?.platform_fee_not_settled_mwk)}
                  </p>
                </div>
                <div className="rounded-lg border border-border/50 p-3">
                  <p className="text-xs text-muted-foreground">PayChangu charges (open)</p>
                  <p className="text-lg font-bold">
                    {formatMwk(settlements.reconciliation?.paychangu_charges_not_settled_mwk)}
                  </p>
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-2 mb-4">
              <select
                value={settlementFilter}
                onChange={(e) => setSettlementFilter(e.target.value)}
                className="px-3 py-2 bg-muted rounded-lg text-sm border border-border"
              >
                <option value="not_settled">Not settled</option>
                <option value="settled">Settled</option>
                <option value="settlement_failed">Failed</option>
                <option value="all">All confirmed payments</option>
              </select>
              <button
                type="button"
                onClick={loadSettlements}
                disabled={settlementsLoading}
                className="inline-flex items-center gap-2 px-3 py-2 bg-muted rounded-lg text-sm"
              >
                {settlementsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                Refresh
              </button>
              <button
                type="button"
                onClick={handleSyncCharges}
                disabled={actionLoading}
                className="inline-flex items-center gap-2 px-3 py-2 bg-zinc-700 text-white rounded-lg text-sm disabled:opacity-50"
              >
                Sync PayChangu charges
              </button>
            </div>

            <div className="flex flex-wrap gap-2 mb-4 items-end">
              <input
                type="text"
                placeholder="Note (bank ref, payout date…)"
                value={settlementNote}
                onChange={(e) => setSettlementNote(e.target.value)}
                className="flex-1 min-w-[200px] px-3 py-2 bg-input/50 border border-border rounded-lg text-sm"
              />
              <button
                type="button"
                onClick={() => handleMarkSettled(false)}
                disabled={actionLoading || selectedRefs.size === 0}
                className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm disabled:opacity-50"
              >
                <CheckCircle className="w-4 h-4" />
                Mark settled ({selectedRefs.size})
              </button>
              <button
                type="button"
                onClick={() => handleMarkSettled(true)}
                disabled={actionLoading || selectedRefs.size === 0}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600/90 text-white rounded-lg text-sm disabled:opacity-50"
              >
                Mark failed
              </button>
            </div>

            {settlements?.notes?.length > 0 && (
              <ul className="text-xs text-muted-foreground mb-4 space-y-1">
                {settlements.notes.map((n, i) => (
                  <li key={i}>• {n}</li>
                ))}
              </ul>
            )}
          </div>

          <div className="bg-card border border-border/50 rounded-xl p-6">
            {settlementsLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-12 loading-shimmer rounded-lg" />
                ))}
              </div>
            ) : settlementsError && !settlements ? (
              <ErrorState error={settlementsError} onRetry={loadSettlements} />
            ) : settlements?.transactions?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border/50">
                      <th className="py-2 px-2 w-8" />
                      <th className="text-left py-2 px-2">Reference</th>
                      <th className="text-left py-2 px-2">Paid</th>
                      <th className="text-right py-2 px-2">Charged</th>
                      <th className="text-right py-2 px-2">Fee 4%</th>
                      <th className="text-right py-2 px-2">PC charges</th>
                      <th className="text-left py-2 px-2">Settlement</th>
                      <th className="text-left py-2 px-2">Paid at</th>
                    </tr>
                  </thead>
                  <tbody>
                    {settlements.transactions.map((tx) => (
                      <tr key={tx.id} className="border-b border-border/30 hover:bg-muted/20">
                        <td className="py-2 px-2">
                          <input
                            type="checkbox"
                            checked={selectedRefs.has(tx.reference)}
                            onChange={() => toggleSettlementSelect(tx.reference)}
                          />
                        </td>
                        <td className="py-2 px-2 font-mono text-xs">{tx.reference}</td>
                        <td className="py-2 px-2">
                          {tx.provider_code}
                          {tx.customer_name && (
                            <div className="text-xs text-muted-foreground">{tx.customer_name}</div>
                          )}
                        </td>
                        <td className="py-2 px-2 text-right">{formatMwk(tx.total_charged_mwk)}</td>
                        <td className="py-2 px-2 text-right">{formatMwk(tx.platform_fee_mwk)}</td>
                        <td className="py-2 px-2 text-right">
                          {tx.paychangu_charges_mwk != null ? formatMwk(tx.paychangu_charges_mwk) : '—'}
                        </td>
                        <td className="py-2 px-2">
                          <span
                            className={`px-2 py-0.5 text-xs rounded ${
                              tx.settlement_status === 'settled'
                                ? 'bg-emerald-500/20 text-emerald-700'
                                : tx.settlement_status === 'settlement_failed'
                                  ? 'bg-red-500/20 text-red-700'
                                  : 'bg-amber-500/20 text-amber-700'
                            }`}
                          >
                            {tx.settlement_status}
                          </span>
                        </td>
                        <td className="py-2 px-2 text-xs text-muted-foreground">
                          {tx.payment_received_at
                            ? new Date(tx.payment_received_at).toLocaleDateString()
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">No transactions for this filter.</p>
            )}
          </div>
        </div>
      )}

      {activeTab === 'customer-queries' && (
        <div className="bg-card border border-border/50 rounded-xl p-6 space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="font-semibold flex items-center gap-2">
                <Search className="w-5 h-5 text-primary" />
                Customer queries
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Look up who made a payment by phone, email, or PayChangu reference when customers contact support.
              </p>
            </div>
            <button
              type="button"
              onClick={() => loadCustomerQueries()}
              disabled={customerLoading}
              className="inline-flex items-center gap-2 px-3 py-2 bg-muted rounded-lg text-sm hover:bg-muted/80 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${customerLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          <form onSubmit={handleCustomerSearchSubmit} className="flex flex-wrap items-center gap-2">
            <input
              type="text"
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              placeholder="Phone, email, or payment reference"
              className="flex-1 min-w-[220px] px-3 py-2 text-sm rounded-lg border border-border/50 bg-background focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
            <button
              type="submit"
              disabled={customerLoading}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-primary text-primary-foreground disabled:opacity-60"
            >
              <Search className="w-4 h-4" />
              Search
            </button>
            {customerSearch && (
              <button
                type="button"
                onClick={() => {
                  setCustomerSearch('');
                  loadCustomerQueries('');
                }}
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                Clear
              </button>
            )}
          </form>

          {customerLoading && !customerQueries ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => <div key={i} className="h-14 loading-shimmer rounded-lg" />)}
            </div>
          ) : customerError && !customerQueries ? (
            <ErrorState error={customerError} onRetry={() => loadCustomerQueries()} />
          ) : customerQueries?.transactions?.length > 0 ? (
            <div className="overflow-x-auto">
              <p className="text-xs text-muted-foreground mb-3">
                Showing {customerQueries.transactions.length} of {customerQueries.total} transaction(s)
                {customerSearch ? ` matching "${customerSearch}"` : ''}
              </p>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/50">
                    <th className="text-left py-3 px-3 text-muted-foreground font-medium">Customer</th>
                    <th className="text-left py-3 px-3 text-muted-foreground font-medium">Phone / Email</th>
                    <th className="text-left py-3 px-3 text-muted-foreground font-medium">Reference</th>
                    <th className="text-left py-3 px-3 text-muted-foreground font-medium">Service</th>
                    <th className="text-left py-3 px-3 text-muted-foreground font-medium">Account</th>
                    <th className="text-right py-3 px-3 text-muted-foreground font-medium">Amount</th>
                    <th className="text-left py-3 px-3 text-muted-foreground font-medium">Status</th>
                    <th className="text-left py-3 px-3 text-muted-foreground font-medium">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {customerQueries.transactions.map((tx) => (
                    <tr key={tx.id} className="border-b border-border/30 hover:bg-muted/30">
                      <td className="py-3 px-3">
                        <div className="font-medium">{tx.user?.full_name || tx.customer_name || '—'}</div>
                        <div className="text-xs text-muted-foreground font-mono">{tx.user?.id || tx.user_id || '—'}</div>
                      </td>
                      <td className="py-3 px-3">
                        <div className="font-mono text-xs">{tx.user?.phone || '—'}</div>
                        <div className="text-xs text-muted-foreground">{tx.user?.email || '—'}</div>
                      </td>
                      <td className="py-3 px-3 font-mono text-xs">{tx.reference}</td>
                      <td className="py-3 px-3">{tx.provider_code}</td>
                      <td className="py-3 px-3 font-mono text-xs">{tx.account_number || '—'}</td>
                      <td className="py-3 px-3 text-right font-semibold">{formatMwk(tx.amount)}</td>
                      <td className="py-3 px-3">
                        <span className={`px-2 py-0.5 text-xs rounded ${
                          tx.status === 'success' ? 'bg-emerald-500/20 text-emerald-600' :
                          tx.status === 'failed' || tx.status === 'expired' ? 'bg-red-500/20 text-red-600' :
                          tx.status === 'refunded' ? 'bg-zinc-500/20 text-zinc-600' :
                          'bg-amber-500/20 text-amber-700'
                        }`}>
                          {tx.status}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-xs text-muted-foreground whitespace-nowrap">
                        {tx.created_at ? new Date(tx.created_at).toLocaleString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">
              {customerSearch ? 'No transactions match that phone, email, or reference.' : 'No transactions yet.'}
            </p>
          )}
        </div>
      )}

      {activeTab === "transactions" && (
        <div className="bg-card border border-border/50 rounded-xl p-6 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="font-semibold">All Transactions</h3>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={loadAdminTransactions}
                disabled={transactionsLoading}
                className="inline-flex items-center gap-2 px-3 py-2 bg-muted rounded-lg text-sm hover:bg-muted/80 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${transactionsLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              {pendingCount > 0 && (
                <button
                  type="button"
                  onClick={handleCompleteAllPending}
                  disabled={actionLoading || transactionsLoading}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-600/90 disabled:opacity-50 text-sm font-medium"
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                  Complete all pending ({pendingCount})
                </button>
              )}
            </div>
          </div>

          {transactionsLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => <div key={i} className="h-16 loading-shimmer rounded-lg" />)}
            </div>
          ) : transactionsError && adminTransactions.length === 0 ? (
            <ErrorState error={transactionsError} onRetry={loadAdminTransactions} />
          ) : adminTransactions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border/50">
                    <th className="text-left py-3 px-4 text-muted-foreground font-medium">Reference</th>
                    <th className="text-left py-3 px-4 text-muted-foreground font-medium">Service</th>
                    <th className="text-left py-3 px-4 text-muted-foreground font-medium">Amount</th>
                    <th className="text-left py-3 px-4 text-muted-foreground font-medium">Status</th>
                    <th className="text-left py-3 px-4 text-muted-foreground font-medium">Token</th>
                    <th className="text-left py-3 px-4 text-muted-foreground font-medium">Date</th>
                    <th className="text-right py-3 px-4 text-muted-foreground font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {adminTransactions.map((tx) => (
                    <tr key={tx.id} className="border-b border-border/30 hover:bg-muted/30">
                      <td className="py-3 px-4 font-mono text-sm">{tx.reference}</td>
                      <td className="py-3 px-4">{tx.provider_code}</td>
                      <td className="py-3 px-4 font-semibold">MWK {tx.amount.toLocaleString()}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 text-xs rounded ${
                          tx.status === "success" ? "bg-emerald-500/20 text-emerald-500" :
                          tx.status === "failed" ? "bg-red-500/20 text-red-500" :
                          "bg-yellow-500/20 text-yellow-500"
                        }`}>{tx.status}</span>
                      </td>
                      <td className="py-3 px-4 font-mono text-xs text-emerald-600">{tx.token || "—"}</td>
                      <td className="py-3 px-4 text-muted-foreground">{new Date(tx.created_at).toLocaleString()}</td>
                      <td className="py-3 px-4 text-right">
                        {tx.status === "pending" && (
                          <button
                            type="button"
                            onClick={() => handleCompleteOne(tx.id)}
                            disabled={actionLoading}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-600 text-white text-xs rounded-lg hover:bg-emerald-600/90 disabled:opacity-50"
                          >
                            {actionLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
                            Complete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">No transactions yet</p>
          )}
        </div>
      )}
    </motion.div>
  );
};

export default AdminDashboard;